import * as React from "react";
import { useState, useCallback } from "react";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import {
  ModifiedApplicationContentType,
  ApplicationEntity,
  ModifyInfoType,
} from "../../../../../application/domain/application/application-entity";
import { ApplicationHistory } from "../../../../../application/domain/application/application-history";
import { matchType } from "../../../../../application/domain/base/base-type";
import { Information } from "./information";
import { Modification } from "./modification";
import { ApiLoadingState } from "../../../../../application/domain/api-loading-state";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { CSVEncoding } from "../../../../../application/domain/file-encoding";

type Props = {
  proc: ProcedureEntity;
  application: ApplicationEntity;
  histories: GrafferGqlResult<ApplicationHistory[]>;
  outputPDF: () => void;
  outputPDFForCustomer: () => void;
  isPDFOutputting: boolean;
  modifyingProcess: ApiLoadingState;
  onModify: (
    content: ModifiedApplicationContentType,
    modifyInfo: ModifyInfoType,
  ) => void;
  onModificationEnd: () => void;
  onBulkDownload: () => void;
  isBulkDownloading: boolean;
  selectMetaIds: Array<string>;
  toggleCsvMetaCheck: (id: string) => void;
  toggleAllCsvMetaCheck: (checked: boolean) => void;
  downloadSingleRecordCSV: () => Promise<void>;
  csvEncoding: CSVEncoding;
  onChangeCSVEncoding: (encoding: CSVEncoding) => void;
  includeTenantProcessResult: boolean;
  onCheckIncludeTenantProcessResult: (checked: boolean) => void;
  requireSelectableRawValue: boolean;
  onChangeRequireSelectableRawValue: (isRequired: boolean) => void;
};

type Mode = "view" | "modify";

export const ApplyFormInfoModification: React.FC<Props> = ({
  proc,
  application,
  histories,
  outputPDF,
  outputPDFForCustomer,
  isPDFOutputting,
  modifyingProcess,
  onModify,
  onModificationEnd,
  onBulkDownload,
  isBulkDownloading,
  selectMetaIds,
  toggleCsvMetaCheck,
  toggleAllCsvMetaCheck,
  downloadSingleRecordCSV,
  csvEncoding,
  onChangeCSVEncoding,
  includeTenantProcessResult,
  onCheckIncludeTenantProcessResult,
  requireSelectableRawValue,
  onChangeRequireSelectableRawValue,
}) => {
  const [mode, setMode] = useState<Mode>("view");
  const onModificationEndImpl = useCallback(() => {
    onModificationEnd();
    setMode("view");
  }, [onModificationEnd, setMode]);
  return matchType(mode, {
    view: (
      <Information
        application={application}
        proc={proc}
        histories={histories}
        isPDFOutputting={isPDFOutputting}
        outputPDF={outputPDF}
        outputPDFForCustomer={outputPDFForCustomer}
        onModify={() => setMode("modify")}
        onBulkDownload={onBulkDownload}
        isBulkDownloading={isBulkDownloading}
        selectedMetaIds={selectMetaIds}
        toggleCsvMetaCheck={toggleCsvMetaCheck}
        toggleAllCsvMetaCheck={toggleAllCsvMetaCheck}
        downloadSingleRecordCSV={downloadSingleRecordCSV}
        csvEncoding={csvEncoding}
        onChangeCSVEncoding={onChangeCSVEncoding}
        includeTenantProcessResult={includeTenantProcessResult}
        onCheckIncludeTenantProcessResult={onCheckIncludeTenantProcessResult}
        requireSelectableRawValue={requireSelectableRawValue}
        onChangeRequireSelectableRawValue={onChangeRequireSelectableRawValue}
      />
    ),
    modify: (
      <Modification
        procedure={proc.published!}
        application={application}
        modifyingProcess={modifyingProcess}
        onModify={onModify}
        onModificationEnd={onModificationEndImpl}
      />
    ),
  });
};
